import React, { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Skeleton, Spin, Card } from 'antd';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const PdfViewer = ({ pdfUrl, containerScale = 1 }) => {
    const [numPages, setNumPages] = useState(null);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
    const [scale, setScale] = useState(1);

    const containerRef = useRef(null);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    const onPageLoadSuccess = (page) => {
        const viewport = page.getViewport({ scale: 1 });
        if (!pageSize.width || !pageSize.height) {
            setPageSize({ width: viewport.width, height: viewport.height });
        }
        setPageLoaded(true);
    };

    // Responsive scaling
    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current && pageSize.width) {
                const containerWidth = containerRef.current.offsetWidth * 0.98;
                const newScale = containerWidth / pageSize.width;
                setScale(newScale);
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, [pageSize]);

    useEffect(() => {
        if (pageSize.width > 0) {
            const timer = setTimeout(() => {
                if (containerRef.current) {
                    const containerWidth = containerRef.current.offsetWidth * 0.98;
                    const newScale = containerWidth / pageSize.width;
                    setScale(newScale);
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [pageSize]);

    const pdfSource = pdfUrl || '/assets/Formatted_Feedback_QC_Feature.pdf';

    return (
        <div
            ref={containerRef}
            style={{
                transform: `scale(${containerScale})`,
                transformOrigin: "top left",
                transition: "transform 0.3s ease-in-out",
                width: '100%',
                overflow: 'hidden',
            }}
        >
            <Document
                file={pdfSource}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={<Spin tip="Loading PDF..." size="large" style={{ display: 'block', margin: '2rem auto' }} />}
                error={<div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>Failed to load PDF!</div>}
            >
                {numPages ? (
                    Array.from(new Array(numPages), (_, index) => (
                        <Card
                            key={index}
                            style={{ marginBottom: 24, borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                            bodyStyle={{ padding: 0 }}
                        >
                            <Page
                                key={`page_${index + 1}`}
                                pageNumber={index + 1}
                                scale={scale}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                                onLoadSuccess={onPageLoadSuccess}
                                loading={
                                    <Skeleton
                                        active
                                        paragraph={{ rows: 12 }}
                                        style={{
                                            height: pageSize.height ? pageSize.height * scale : 400,
                                            padding: 24,
                                        }}
                                    />
                                }
                            />
                        </Card>
                    ))
                ) : (
                    <Skeleton active paragraph={{ rows: 10 }} style={{ padding: 24 }} />
                )}
            </Document>

            {!pageLoaded && numPages === null && (
                <div style={{ textAlign: 'center', padding: '2rem' }}>Loading PDF document...</div>
            )}
        </div>
    );
};

export default PdfViewer;
